/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import {
	ArrowButton,
	Background,
	BoxContainer,
	Character,
	Check,
	ClubName,
	Container,
	CustomButtom,
	DataContainer,
	InfoButton,
	Message,
	MessageContainer,
	RewardAmout,
	RewardContainer,
	RewardItem,
	StarBurst,
	Subtitle,
	TierContainer,
	TierTitle,
	TierTitleContainer,
	Title,
	TitlesContainer,
	Wrapper
} from "./styles";
import CountdownV2 from "components/CountdownV2";
import infoIcon from "assets/header-banner/info-button.svg";
import checkIcon from "assets/check-icon.png";
import { useMediaQuery } from "@mui/material";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import ProgressBar from "./lib/ProgressBar";
import { ClubGiftsGaloreTracker } from "interfaces/club-gifts-galore-v2/index";
import { getVariantImage } from "helpers/getVariantImage";
import useRewardsById from "hooks/useRewardsById";
import { useAppDispatch, useImagePath } from "hooks";
import { clubGiftsGaloreAssets } from "assets/cdn";
import { onOpenPopUp } from "redux/slices";
import { useUpdatePopupStateMutation } from "redux/apis/clubGiftsGalore";
import {
	featureTrackerAPI,
	offersApi,
	usePostMetadataMutation
} from "redux/apis";
import { FeatureType } from "interfaces/feature-tracker";
import { IClubGift } from "contexts/WebStoreProvider/types";

interface GiftsGaloreCardProps {
	tracker: ClubGiftsGaloreTracker;
	refetch: () => void;
	isPopupsOpen: boolean;
	giftBox: IClubGift | undefined;
}

export const GiftsGaloreCard: React.FC<GiftsGaloreCardProps> = ({
	tracker,
	refetch,
	isPopupsOpen,
	giftBox
}) => {
	const isPortrait = useMediaQuery("(max-width:649px)");
	const { getImage } = useImagePath("cdn");
	const { card: cardAssets } = clubGiftsGaloreAssets.v2;
	const backArrowIcon = getImage(cardAssets.arrowBack);
	const forwardArrowIcon = getImage(cardAssets.arrowForward);
	const leftChar = getImage(cardAssets.charLeft);
	const rightChar = getImage(cardAssets.charRight);
	const starBurst = getImage(cardAssets.starBurst);
	const boxImage = getImage(cardAssets.box);
	const tierTitleImage = getImage(cardAssets.tierTitle);
	const dispatch = useAppDispatch();
	const [postMetadata] = usePostMetadataMutation();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { nowTime, userTracker, syncUserTracker } = useWebStoreContext();
	const { authToken } = useWebStoreContext();
	const { isFetching }: { isFetching: boolean } =
		featureTrackerAPI.endpoints.getFeatureTracker.useQueryState({
			FeatureType: FeatureType.CLUB_GIFT_BONUS_V2,
			isAuthenticated: !!authToken?.value
		});
	const [activeTier, setActiveTier] = useState(
		tracker?.tiers[tracker?.activeTierIndex]
	);
	const [currentTier, setCurrentTier] = useState(activeTier);
	const isCurrentTierActive = currentTier?.tierIndex === activeTier?.tierIndex;
	const lastTier = tracker?.tiers[tracker?.tiers.length - 1];
	const lastCompleteTier = tracker?.tiers.findLast((tier) => tier.complete);
	const reward = useRewardsById(currentTier?.rewardId as string);
	const [updatePopupState, { isLoading: updatePopupStateLoading }] =
		useUpdatePopupStateMutation();
	const [initialContributionCount, setInitialContributionCount] = useState<
		null | number
	>(null);
	const [hasSeenFtue, setHasSeenFtue] = useState<boolean>(false);
	const tierCompletePopupTriggered = useRef(new Set<string>());
	const hasNotEnoughClubMembers =
		currentTier?.tierIndex === activeTier?.tierIndex &&
		!!currentTier?.numNewMembersNeeded;
	const hasUserContributed =
		currentTier &&
		tracker?.userClubTracker.tierContributionCounts[currentTier?.tierIndex] ===
			tracker?.tiers[currentTier?.tierIndex].maxUserContributionCount &&
		!tracker?.tiers[currentTier?.tierIndex].complete;

	const invalidateGiftBoxes = () => {
		dispatch(
			offersApi.util.invalidateTags([
				{
					type: "offersTag",
					id: "clubGift"
				}
			])
		);
	};

	const navigateTiers = (direction: "next" | "prev") => {
		const newTierIndex =
			direction === "next"
				? Math.min(currentTier.tierIndex + 1, tracker.tiers.length - 1)
				: Math.max(currentTier.tierIndex - 1, 0);

		setCurrentTier(tracker.tiers[newTierIndex]);
	};

	const setPopupAsSeen = async (popupType: string, tierIndex?: number) => {
		await updatePopupState({
			popupType,
			body: {
				tierIndex
			}
		});
	};

	//* Set Google analaytics
	useEffect(() => {
		reportEvent("club_shop_event", {
			source: "club_gifts_galore",
			club_gifts_galore_id: tracker?.clubGiftId,
			progress: activeTier?.contributionCount,
			maximum_threshold: activeTier?.requiredContributionCount,
			more_club_members_needed_to_win: hasNotEnoughClubMembers,
			current_tier: tracker?.activeTierIndex + 1,
			max_individual_contribution_limit: activeTier?.maxUserContributionCount
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "club_shop_event",
			event_type: "club_gift_galore_v2",
			action: "club_shop_event",
			source: "club_gifts_galore",
			tag_type: hasNotEnoughClubMembers,
			event_id: tracker?.clubGiftId,
			confignodecount: activeTier?.maxUserContributionCount,
			progress_status: activeTier?.contributionCount.toString(),
			progress_goal: activeTier?.requiredContributionCount.toString(),
			loopcount: tracker?.activeTierIndex + 1
		});
	}, []);

	// *Updates the current tier
	useEffect(() => {
		setCurrentTier(tracker?.tiers[currentTier?.tierIndex]);
	}, [tracker?.tiers]);

	// *Set the initial user contribution count of the active tier
	useEffect(() => {
		activeTier &&
			setInitialContributionCount(
				tracker?.userClubTracker?.tierContributionCounts[activeTier.tierIndex]
			);
	}, [activeTier]);

	// *Auto move to active tier - v1
	// useEffect(() => {
	// 	setCurrentTier(activeTier ?? lastCompleteTier);
	// }, [activeTier]);

	// *Auto move to active tier - v2
	useEffect(() => {
		if (isPopupsOpen) {
			return;
		}
		const timeoutId = setTimeout(() => {
			setCurrentTier(activeTier ?? lastCompleteTier);
		}, 1500);
		return () => clearTimeout(timeoutId);
	}, [activeTier, isPopupsOpen]);

	// *Trigger last completed tier popup
	useEffect(() => {
		const tierIndex = lastCompleteTier?.tierIndex;
		if (tierIndex === undefined) {
			return;
		}
		const popupKey = `${tracker.userClubTracker.userId}:${tracker.eventId}:${tierIndex}`;
		if (
			lastCompleteTier &&
			!tracker?.userClubTracker?.popupTrackers.tierCompletePopupDisplayed[
				lastCompleteTier.tierIndex
			] &&
			!isPopupsOpen &&
			!updatePopupStateLoading &&
			!isFetching &&
			!tierCompletePopupTriggered.current.has(popupKey)
		) {
			const popupType =
				lastCompleteTier === lastTier ? "cgg-event-completed" : "cgg-new-tier";
			tierCompletePopupTriggered.current.add(popupKey);
			dispatch(onOpenPopUp(popupType));
			setPopupAsSeen("tier-complete-popup-displayed", tierIndex);
		}
	}, [currentTier, isPopupsOpen]);

	//* Trigger contribution popup just before last completed level popup & Set new active tier
	useEffect(() => {
		if (
			!(!lastCompleteTier || initialContributionCount === null) &&
			tracker?.userClubTracker.tierContributionCounts[
				lastCompleteTier?.tierIndex
			] > initialContributionCount &&
			tracker?.userClubTracker.tierContributionCounts[
				lastCompleteTier?.tierIndex
			] <= lastCompleteTier?.maxUserContributionCount &&
			lastCompleteTier?.contributionCount ===
				lastCompleteTier?.requiredContributionCount
		) {
			dispatch(
				onOpenPopUp(`cgg-purchase-success::${lastCompleteTier.tierIndex}`)
			);
		}
		//setActiveTier(tracker?.tiers[tracker?.activeTierIndex]);
	}, [tracker?.activeTierIndex]);

	// *Updates the active tier
	useEffect(() => {
		setActiveTier(tracker?.tiers[tracker?.activeTierIndex]);
	}, [tracker?.activeTierIndex, tracker?.tiers]);

	// *Trigger contribution popup
	useEffect(() => {
		if (!activeTier || initialContributionCount === null) {
			return;
		}
		if (
			tracker?.userClubTracker.tierContributionCounts[activeTier?.tierIndex] >
				initialContributionCount &&
			tracker?.userClubTracker.tierContributionCounts[activeTier?.tierIndex] <=
				activeTier?.maxUserContributionCount &&
			activeTier?.contributionCount < activeTier?.requiredContributionCount
		) {
			dispatch(onOpenPopUp(`cgg-purchase-success::${activeTier.tierIndex}`));
		}
	}, [initialContributionCount, activeTier?.contributionCount]);

	// *Trigger club hopper popup
	useEffect(() => {
		if (
			tracker.userClubTracker.clubHopped &&
			!tracker.userClubTracker.popupTrackers.clubHopPopupDisplayed
		) {
			dispatch(onOpenPopUp("cgg-club-hopper"));
			setPopupAsSeen("club-hop-popup-displayed");
		}
	}, [
		tracker.userClubTracker.clubHopped,
		tracker.userClubTracker.popupTrackers.clubHopPopupDisplayed
	]);

	// *Update metadata for ClubGiftGaloreFtue popup
	useEffect(() => {
		if (
			!userTracker?.metadata?.hasDisplayedClubGiftGaloreFtue &&
			!isPopupsOpen &&
			!hasSeenFtue
		) {
			dispatch(onOpenPopUp("cgg-more-info"));
			setHasSeenFtue(true);
			postMetadata({
				key: "hasDisplayedClubGiftGaloreFtue",
				value: "true"
			}).unwrap();
			syncUserTracker();
		}
	}, [isPopupsOpen]);

	return (
		<Container>
			<Background
				className={tracker?.complete ? "event-completed" : ""}
				imageUrl={getVariantImage(
					tracker?.backgroundImageUrl || "",
					"{club_gifts_galore_variant}",
					isPortrait ? "small" : "large"
				)}
			/>
			{!tracker?.complete && (
				<CountdownV2
					text="Event ends in:"
					startTime={nowTime}
					endTime={tracker?.endTime || ""}
					onEnd={() => {
						invalidateGiftBoxes();
						refetch();
					}}
					style={{
						padding: "0.7em 0.8em",
						top: "1em",
						left: "1em",
						fontSize: "0.7em"
					}}
					iconStyle={{ width: "1.9em", height: "1.9em" }}
					labelStyle={{ fontSize: "1.4em" }}
				/>
			)}
			<InfoButton
				src={infoIcon}
				onClick={() => {
					dispatch(onOpenPopUp("cgg-more-info"));
				}}
			/>
			{!isPortrait && (
				<ClubName>
					Club: <b style={{ color: "black" }}>{tracker?.clubName}</b>
				</ClubName>
			)}
			<DataContainer className={tracker?.complete ? "event-completed" : ""}>
				<TitlesContainer>
					<Title
						className={tracker?.complete ? "event-completed" : ""}
						src={getVariantImage(
							tracker?.titleImageUrl || "",
							"{club_gifts_galore_variant}",
							isPortrait ? "small" : "large"
						)}
					/>
					{tracker?.complete && (
						<>
							<Subtitle style={{ paintOrder: "stroke fill" }}>
								Congratulations!
							</Subtitle>
							<Subtitle
								style={{ fontSize: "1.4em", paintOrder: "stroke fill" }}
							>
								Event Completed!
							</Subtitle>
						</>
					)}
				</TitlesContainer>
				{currentTier && !tracker?.complete && (
					<TierContainer>
						{isPortrait && (
							<ClubName>
								Club: <b style={{ color: "black" }}>{tracker?.clubName}</b>
							</ClubName>
						)}
						<BoxContainer
							$backgroundImage={boxImage}
							className={currentTier?.complete ? "complete" : ""}
						>
							<TierTitleContainer $backgroundImage={tierTitleImage}>
								<TierTitle>Tier {currentTier.tierIndex + 1}</TierTitle>
							</TierTitleContainer>
							<Wrapper>
								<Check
									src={checkIcon}
									style={{ display: currentTier?.complete ? "block" : "none" }}
								/>
								<RewardContainer
									key={currentTier?.tierIndex}
									className={currentTier?.complete ? "complete" : ""}
								>
									<RewardItem
										src={reward[0]?.imageUrl || reward[0]?.overrideImageUrl}
									/>
									<RewardAmout>
										{reward[0]?.description &&
											new Intl.NumberFormat("en-US", {
												notation: "compact",
												compactDisplay: "short",
												maximumFractionDigits: 1,
												roundingMode: "floor"
											}).format(
												Number(reward[0]?.description?.replaceAll(",", ""))
											)}
									</RewardAmout>
									<StarBurst
										className={isCurrentTierActive ? "active" : ""}
										src={starBurst}
									/>
								</RewardContainer>
							</Wrapper>
							<ArrowButton
								src={backArrowIcon}
								onClick={() => navigateTiers("prev")}
								style={{
									display: currentTier.tierIndex > 0 ? "block" : "none"
								}}
							></ArrowButton>

							<ArrowButton
								className="forward"
								src={forwardArrowIcon}
								onClick={() => navigateTiers("next")}
								style={{
									display:
										currentTier.tierIndex < tracker?.tiers.length - 1
											? "block"
											: "none"
								}}
							></ArrowButton>

							{isPortrait && (
								<>
									<Character src={leftChar} />
									<Character className="right" src={rightChar} />
								</>
							)}
							<ProgressBar
								isCurrentTierActive={isCurrentTierActive}
								currentUsers={currentTier?.contributionCount}
								totalUsers={currentTier?.requiredContributionCount}
								boxIconImageUrl={giftBox?.chestIconImageUrl}
							/>
						</BoxContainer>
						<CustomButtom
							variant="contained"
							color="success"
							size="large"
							onClick={() => {
								const giftBoxesElement = document.getElementById("gift-boxes");
								window.scrollTo({
									top: giftBoxesElement?.offsetTop || 0,
									behavior: "smooth"
								});
							}}
						>
							GO NOW!
						</CustomButtom>
					</TierContainer>
				)}
			</DataContainer>
			{!isPortrait && !tracker?.complete && (
				<>
					<Character src={leftChar} />
					<Character className="right" src={rightChar} />
				</>
			)}
			{hasNotEnoughClubMembers ? (
				<MessageContainer>
					<Message>
						Your Club needs just {currentTier?.numNewMembersNeeded} more{" "}
						{currentTier?.numNewMembersNeeded > 1 ? "members" : "member"} to
						qualify!
					</Message>
					<InfoButton
						className="message"
						src={infoIcon}
						onClick={() => {
							dispatch(onOpenPopUp("cgg-not-enough-club-members"));
						}}
					/>
				</MessageContainer>
			) : (
				hasUserContributed && (
					<MessageContainer>
						<Message>You have already contributed to this Tier!</Message>
						<InfoButton
							className="message"
							src={infoIcon}
							onClick={() => {
								dispatch(onOpenPopUp("cgg-eligibility-tools"));
							}}
						/>
					</MessageContainer>
				)
			)}
		</Container>
	);
};
